import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardContent, Stack, TextField, Typography, CircularProgress } from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { EMAIL_REGEX, URL_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { apiAuth } from "../../services/AllServices";

function GlobalSetting() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiAuth.getSetting();
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);

  const initialValues = {
    instaLink: data?.instaLink ?? "",
    fbLink: data?.fbLink ?? "",
    twitterLink: data?.twitterLink ?? "",
    linkedInLink: data?.linkedInLink ?? "",
    contactUsEmail: data?.contactUsEmail ?? "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string()
      .trim()
      .required("Instagram Link is required")
      .matches(URL_REGEX, "Instagram link must be in the format: https://<domain>.com/"),
    // .max(255, "Instagram link must be no more than 255 characters")

    fbLink: Yup.string()
      .trim()
      // .max(255, "Facebook link must be no more than 255 characters")
      .required("Facebook Link is required")
      .matches(URL_REGEX, "Facebook link must be in the format: https://<domain>.com/"),

    twitterLink: Yup.string()
      .trim()
      // .max(255, "Twitter link must be no more than 255 characters")
      .required("Twitter Link is required")
      .matches(URL_REGEX, "Twitter link must be in the format: https://<domain>.com/"),

    linkedInLink: Yup.string()
      .trim()
      // .max(255, "LinkedIn must be no more than 255 characters")
      .required("LinkedIn Link is required")
      .matches(URL_REGEX, "LinkedIn link must be in the format: https://<domain>.com/"),


    contactUsEmail: Yup.string()
      .required('Contact Us Email is required')
      .trim()
      .matches(EMAIL_REGEX, "Please enter a valid email address")
      .max(255),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      values.instaLink = values?.instaLink?.trim();
      values.fbLink = values?.fbLink?.trim();
      values.linkedInLink = values?.linkedInLink?.trim();
      values.twitterLink = values?.twitterLink?.trim();
      values.contactUsEmail = values?.contactUsEmail?.trim();

      apiAuth.updateSetting(values)
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message);
          }
          setSubmitting(false);
          settingData();
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <Card>
      <CardContent>
        <FormikProvider value={formik} style={{ padding: "34px 30px" }}>
          <Typography variant="h4" sx={{ mb: 2 }} align="start">
            Settings
          </Typography>
          {loading ? <CircularProgress /> : (
            <Form>
              <Stack spacing={3}>

                <TextField
                  name="fbLink"
                  label="Facebook Link *"
                  {...getFieldProps("fbLink")}
                  error={Boolean(touched.fbLink && errors.fbLink)}
                  helperText={touched.fbLink && errors.fbLink}
                />

                <TextField
                  name="twitterLink"
                  label="Twitter Link *"
                  {...getFieldProps("twitterLink")}
                  error={Boolean(touched.twitterLink && errors.twitterLink)}
                  helperText={touched.twitterLink && errors.twitterLink}
                />

                <TextField
                  name="instaLink"
                  label="Instagram Link *"
                  fullWidth
                  {...getFieldProps("instaLink")}
                  error={Boolean(touched.instaLink && errors.instaLink)}
                  helperText={touched.instaLink && errors.instaLink}
                />

                <TextField
                  name="linkedInLink"
                  label="LinkedIn Link *"
                  {...getFieldProps("linkedInLink")}
                  error={Boolean(touched.linkedInLink && errors.linkedInLink)}
                  helperText={touched.linkedInLink && errors.linkedInLink}
                />

                <TextField
                  name="contactUsEmail"
                  label="Contact Us Email *"
                  {...getFieldProps("contactUsEmail")}
                  error={Boolean(touched.contactUsEmail && errors.contactUsEmail)}
                  helperText={touched.contactUsEmail && errors.contactUsEmail}
                />
              </Stack>

              <Button size="large" type="submit" variant="contained" disabled={isSubmitting} sx={{ my: 2 }}>
                Update
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  if (!isSubmitting) navigate('/dashboard');
                }}
                sx={{ my: 2, ml: 4 }}
              >
                Cancel
              </Button>
            </Form>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
